import React from "react";
import { useTranslation } from "react-i18next";
import type { WizardState } from "./OnboardingWizard";

interface Props {
  data: WizardState["settings"];
  onUpdate: (data: WizardState["settings"]) => void;
  onNext: () => void;
}

const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "Europe/London (GMT)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET)" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam (CET)" },
  { value: "Europe/Warsaw", label: "Europe/Warsaw (CET)" },
  { value: "US/Eastern", label: "US/Eastern (EST)" },
  { value: "US/Central", label: "US/Central (CST)" },
  { value: "US/Pacific", label: "US/Pacific (PST)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST)" },
];

const CURRENCIES = [
  { value: "EUR", label: "EUR - Euro" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "CHF", label: "CHF - Swiss Franc" },
  { value: "PLN", label: "PLN - Polish Zloty" },
  { value: "SEK", label: "SEK - Swedish Krona" },
  { value: "NOK", label: "NOK - Norwegian Krone" },
  { value: "DKK", label: "DKK - Danish Krone" },
  { value: "JPY", label: "JPY - Japanese Yen" },
  { value: "AUD", label: "AUD - Australian Dollar" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Francais" },
  { value: "nl", label: "Nederlands" },
  { value: "pl", label: "Polski" },
  { value: "es", label: "Espanol" },
];

const Step2Settings: React.FC<Props> = ({ data, onUpdate, onNext }) => {
  const { t } = useTranslation("auth");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="wizard-step-content">
        <h2>{t("onboarding.settings.title")}</h2>
        <p className="step-description">
          {t("onboarding.settings.description")}
        </p>

        <div className="wizard-form-row">
          <div className="wizard-form-group">
            <label>{t("onboarding.settings.fields.timezone")}</label>
            <select
              value={data.timezone}
              onChange={(e) => onUpdate({ ...data, timezone: e.target.value })}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div className="wizard-form-group">
            <label>{t("onboarding.settings.fields.currency")}</label>
            <select
              value={data.currency}
              onChange={(e) => onUpdate({ ...data, currency: e.target.value })}
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="wizard-form-group">
          <label>{t("onboarding.settings.fields.language")}</label>
          <select
            value={data.language}
            onChange={(e) => onUpdate({ ...data, language: e.target.value })}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="wizard-actions">
        <div />
        <button type="submit" className="btn-primary">
          {t("onboarding.settings.actions.continue")}
        </button>
      </div>
    </form>
  );
};

export default Step2Settings;
